// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { TextField } from '@mui/material';
import { Amulet } from '@daml.js/splice-amulet/lib/Splice/Amulet';
import { CredentialBilling } from '@daml.js/utility-credential-app-v0/lib/Utility/Credential/App/V0/Model/Billing';
import FormDialog from '../../components/Dialog/FormDialog';
import TextInput from '../../components/Form/TextInput';
import { useScanProxyApiClient } from '../../contexts/ScanProxyApiContext';
import useDistributeAmulets from '../../hooks/mutations/credential/billing/useDistributeAmulet';
import useUserService from '../../hooks/queries/credential/onboarding/useUserService';
import useGetLatestAmuletPrice from '../../hooks/queries/useGetLatestAmuletPrice';
import TemplateContract from '../../utils/TemplateContract';
import { appTransferContextWithDisclosures } from '../../utils/appTransferContextWithDisclosures';
import { ERR_DIST_CRED_BILL } from '../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  row: TemplateContract<CredentialBilling> | undefined;
  amulets: TemplateContract<Amulet>[] | undefined;
  totalBalanceCC: number;
};

export const DistributeAmuletBillingDialog = ({
  open,
  onClose,
  row,
  amulets,
  totalBalanceCC,
}: DialogProps) => {
  const [amount, setAmount] = useState<string>('0.00');
  const [validationMessage, setValidationMessage] = useState<string>('');

  const userService = useUserService();
  const scanProxyApi = useScanProxyApiClient();
  const distributeAmulets = useDistributeAmulets();
  const latestAmuletPrice = useGetLatestAmuletPrice();
  const { showBoundary } = useErrorBoundary<Error>();
  const resetState = useCallback(() => {
    setAmount('');
    setValidationMessage('');
  }, [setAmount]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (row !== undefined) {
      if (!userService.data) {
        showBoundary(new Error(ERR_DIST_CRED_BILL, { cause: 'User service is not available.' }));
        return;
      }
      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );

      await distributeAmulets({
        cid: userService.data.contractId,
        meta: {
          disclosedContracts,
        },
        argument: {
          appTransferContext,
          credentialBillingCid: row.contractId,
          amountUsd: amount,
          coinCids: (amulets ?? []).map((a) => a.contractId),
        },
      });
    }
    handleClose();
  }, [
    userService,
    row,
    amount,
    amulets,
    scanProxyApi,
    distributeAmulets,
    handleClose,
    showBoundary,
  ]);

  const validate = (inputAmount: string) => {
    const depositAmountUSD = Number.isNaN(parseFloat(inputAmount)) ? 0 : parseFloat(inputAmount);
    if (depositAmountUSD > 0 && latestAmuletPrice.data) {
      const depositAmountCC = depositAmountUSD / parseFloat(latestAmuletPrice.data);
      if (depositAmountCC > totalBalanceCC) {
        return 'Requested Amount Exceeds Balance';
      }
    }
    return '';
  };

  return (
    <FormDialog
      open={open}
      title="Share Rewards"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Distribute"
      isSubmitDisabled={!amount || parseFloat(amount) <= 0 || !!validationMessage}
    >
      <TextField
        disabled
        placeholder="CredentialBilling recipient"
        defaultValue={row?.payload.holder ?? ''}
        fullWidth
      />
      <TextInput
        setValidationMessage={setValidationMessage}
        validationMessage={validationMessage}
        getValidationMessage={validate}
        type="number"
        label="Amount(USD)"
        value={amount}
        setValue={setAmount}
        required
      />
    </FormDialog>
  );
};
